/**
 * Modal para visualizar etiqueta nutricional MINSAL
 */
import React, { useState, useEffect } from "react";
import {
  Modal,
  Button,
  Space,
  Typography,
  Card,
  Row,
  Col,
  Divider,
  message,
  Spin,
} from "antd";
import { CalculatorOutlined, DownloadOutlined } from "@ant-design/icons";
import { jsPDF } from "jspdf";
import api from "../../api/endpoints";

const { Title, Text } = Typography;

const NutritionLabelModal = ({ visible, recipe, onCancel, onCalculateNutrition }) => {
  const [labelData, setLabelData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [calculating, setCalculating] = useState(false);

  useEffect(() => {
    if (visible && recipe) {
      if (recipe.has_nutritional_calculation) {
        loadLabelData();
      }
    } else {
      setLabelData(null);
    }
  }, [visible, recipe]);

  const loadLabelData = async () => {
    if (!recipe) return;
    
    setLoading(true);
    try {
      const data = await api.recipes.getNutritionLabel(recipe.id);
      setLabelData(data);
    } catch (error) {
      console.error("Error cargando etiqueta:", error);
      if (error.message.includes("no tiene valores nutricionales")) {
        // No mostrar error, solo no cargar datos
      } else {
        message.error("Error al cargar etiqueta nutricional");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCalculate = async () => {
    if (!recipe) return;
    
    setCalculating(true);
    try {
      await api.recipes.calculateNutrition(recipe.id);
      message.success("Nutrición calculada exitosamente");
      await loadLabelData();
      if (onCalculateNutrition) {
        onCalculateNutrition();
      }
    } catch (error) {
      message.error("Error al calcular nutrición");
    } finally {
      setCalculating(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (!recipe || !labelData) return;
    
    try {
      setLoading(true);
      
      // Obtener ingredientes de la receta
      let ingredients = recipe.ingredients || [];
      if (ingredients.length === 0) {
        try {
          ingredients = await api.recipes.listIngredients(recipe.id);
        } catch (err) {
          console.warn("No se pudieron cargar los ingredientes:", err);
        }
      }
      
      // Crear PDF con medidas exactas del backend: 62mm x 100mm (portrait)
      const doc = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: [62, 100],
        compress: true,
      });
      
      // Configuración (en mm, más fácil de trabajar)
      const margin = 2; // 2mm de margen
      const availableWidth = 100 - (2 * margin); // 96mm de ancho disponible
      let yPos = margin;
      const lineHeight = 3;
      const smallLineHeight = 2.5;
      
      // Configurar fuente y color
      doc.setDrawColor(0, 0, 0);
      doc.setTextColor(0, 0, 0);
      doc.setLineWidth(0.15);
      
      // ========== NOMBRE DEL PRODUCTO ==========
      doc.setFont("helvetica", "bold");
      doc.setFontSize(9);
      const productName = labelData.recipe_name || recipe.name || "Producto";
      const nameLines = doc.splitTextToSize(productName, availableWidth);
      const nameY = yPos + (nameLines.length > 1 ? 2 : 0);
      doc.text(nameLines, 31, nameY, { align: "center" });
      yPos += (nameLines.length * lineHeight) + 0.5;
      
      // Línea separadora
      doc.line(margin, yPos, availableWidth + margin, yPos);
      yPos += lineHeight * 0.5;
      
      // ========== VOLUMEN ==========
      doc.setFont("helvetica", "normal");
      doc.setFontSize(6);
      const totalYield = recipe.total_yield_grams || labelData.total_yield_grams || 0;
      const liters = totalYield / 1000;
      const volumeText = `Volumen: ${liters.toFixed(1)} lts`;
      doc.text(volumeText, margin, yPos);
      yPos += smallLineHeight;
      
      // ========== CONTIENE ==========
      const containsText = "Contiene: Leche";
      doc.text(containsText, margin, yPos);
      yPos += smallLineHeight;
      
      // ========== DURACIÓN ==========
      doc.text("Duración: 120 días a -18°", margin, yPos);
      yPos += smallLineHeight + 0.5;
      
      // Línea separadora
      doc.line(margin, yPos, availableWidth + margin, yPos);
      yPos += lineHeight * 0.5;
      
      // ========== INGREDIENTES ==========
      doc.setFont("helvetica", "bold");
      doc.setFontSize(6);
      doc.text("Ingredientes:", margin, yPos);
      yPos += smallLineHeight;
      
      doc.setFont("helvetica", "normal");
      doc.setFontSize(5.5);
      
      // Construir texto de ingredientes ordenados por cantidad
      let ingredientsText = "";
      if (ingredients.length > 0) {
        // Ordenar por cantidad (convertir a gramos para comparar)
        const sortedIngredients = [...ingredients].sort((a, b) => {
          const qtyA = parseFloat(a.quantity || 0) * (a.unit === "kg" ? 1000 : a.unit === "l" ? 1000 : 1);
          const qtyB = parseFloat(b.quantity || 0) * (b.unit === "kg" ? 1000 : b.unit === "l" ? 1000 : 1);
          return qtyB - qtyA; // Mayor a menor
        });
        
        ingredientsText = sortedIngredients
          .map(ing => {
            const name = ing.name || ing.product?.name || "Ingrediente";
            const qty = parseFloat(ing.quantity || 0);
            const unit = ing.unit || "g";
            const qtyStr = qty === Math.floor(qty) ? qty.toString() : qty.toFixed(1);
            return `${name} (${qtyStr} ${unit})`;
          })
          .join(", ");
      } else {
        ingredientsText = "Ingredientes no especificados";
      }
      
      // Dividir texto en líneas que caben
      const ingredientLines = doc.splitTextToSize(ingredientsText, availableWidth);
      for (const line of ingredientLines) {
        if (yPos < 62 - margin - 3) {
          doc.text(line, margin, yPos);
          yPos += smallLineHeight * 0.9;
        }
      }
      yPos += smallLineHeight * 0.5;
      
      // ========== ADVERTENCIA DE ALÉRGENOS ==========
      doc.setFont("helvetica", "italic");
      doc.setFontSize(5.5);
      const allergenWarning = "Puede contener trazas de soya, maní, nueces, huevo.";
      const allergenLines = doc.splitTextToSize(allergenWarning, availableWidth);
      
      for (const line of allergenLines) {
        if (yPos < 62 - margin - 3) {
          doc.text(line, margin, yPos);
          yPos += smallLineHeight * 0.9;
        }
      }
      yPos += lineHeight * 0.5;
      
      // Línea separadora
      doc.line(margin, yPos, availableWidth + margin, yPos);
      yPos += lineHeight * 0.5;
      
      // ========== INFORMACIÓN DEL ELABORADOR ==========
      doc.setFont("helvetica", "normal");
      doc.setFontSize(4.5);
      const elaboratorText = "Elaborado por Bizantino Gelato";
      const elaboratorLines = doc.splitTextToSize(elaboratorText, availableWidth);
      
      for (const line of elaboratorLines) {
        if (yPos < 62 - margin - 3) {
          doc.text(line, margin, yPos);
          yPos += smallLineHeight * 0.8;
        }
      }
      
      yPos += smallLineHeight * 0.5;
      
      // ========== FECHA DE ELABORACIÓN ==========
      doc.setFont("helvetica", "normal");
      doc.setFontSize(5.5);
      doc.text("Fecha elaboración:", margin, yPos);
      yPos += smallLineHeight + 0.5;
      
      // Línea separadora
      doc.line(margin, yPos, availableWidth + margin, yPos);
      yPos += lineHeight * 0.5;
      
      // ========== INFORMACIÓN NUTRICIONAL ==========
      doc.setFont("helvetica", "bold");
      doc.setFontSize(7);
      const nutritionTitle = "INFORMACIÓN NUTRICIONAL";
      doc.text(nutritionTitle, 31, yPos, { align: "center" });
      yPos += lineHeight + 0.5;
      
      // Porción con mejor formato
      doc.setFont("helvetica", "normal");
      doc.setFontSize(5.5);
      const servingSize = 120; // gramos por porción
      doc.text(`Porción: 1 vaso (${servingSize} g)`, margin, yPos);
      yPos += smallLineHeight + 0.5;
      
      const servingsPerPackage = recipe.servings || 30;
      doc.text(`Porciones por envase: ${servingsPerPackage}`, margin, yPos);
      yPos += smallLineHeight + 0.5;
      
      // Tabla nutricional - Encabezado con mejor espaciado y diseño
      doc.setFont("helvetica", "bold");
      doc.setFontSize(5.5);
      
      // Definir posiciones de columnas con mejor espaciado
      // Ancho disponible: 96mm (100mm - 2*2mm de margen)
      // Distribución más equilibrada: Componente (40mm) | Espacio (3mm) | 100g (25mm) | Espacio (3mm) | Porción (25mm)
      const col1X = margin; // Columna de componentes (izquierda)
      const col1Width = 40; // Ancho máximo para componentes
      const col2X = margin + 43; // Columna de 100g (centro) - empieza después de componentes + espacio
      const col3X = availableWidth + margin; // Columna de Porción (derecha, alineada a la derecha)
      
      // Encabezados de columna con mejor espaciado
      doc.text("Componente", col1X, yPos);
      doc.text("100 g", col2X, yPos);
      doc.text("Porción", col3X, yPos, { align: "right" });
      yPos += smallLineHeight + 0.3;
      
      // Línea bajo encabezado más gruesa
      doc.setLineWidth(0.2);
      doc.line(margin, yPos, availableWidth + margin, yPos);
      doc.setLineWidth(0.15); // Volver al ancho normal
      yPos += smallLineHeight * 0.7;
      
      // Datos nutricionales
      doc.setFont("helvetica", "normal");
      doc.setFontSize(5);
      
      const nutritionPer100g = labelData.values_per_100g || {};
      
      // Función para agregar fila nutricional con mejor formato y espaciado
      const addNutritionRow = (label, fieldKey) => {
        if (yPos > 62 - margin - 2) return;
        
        const value100g = nutritionPer100g[fieldKey] || 0;
        const valueServing = (value100g * servingSize) / 100;
        
        // Formatear valores de manera más legible
        const formatValue = (val) => {
          if (val === null || val === undefined || isNaN(val)) return "-";
          const num = parseFloat(val);
          if (num === 0) return "0";
          if (num < 0.01) return "<0.01";
          if (num < 1) {
            // Para valores pequeños, mostrar 2 decimales
            return num.toFixed(2).replace(/\.?0+$/, "");
          }
          if (num === Math.floor(num)) {
            // Si es entero, sin decimales
            return num.toString();
          }
          // Para valores mayores, 1 decimal máximo
          return num.toFixed(1).replace(/\.?0+$/, "");
        };
        
        const value100gStr = formatValue(value100g);
        const valueServingStr = formatValue(valueServing);
        
        // Componente (izquierda) - truncar si es muy largo para que no se corte
        const maxLabelWidth = col1Width - 2; // Dejar 2mm de margen
        let labelToShow = label;
        const labelWidth = doc.getTextWidth(label);
        
        if (labelWidth > maxLabelWidth) {
          // Si el label es muy largo, truncarlo
          let truncated = label;
          while (doc.getTextWidth(truncated + "...") > maxLabelWidth && truncated.length > 0) {
            truncated = truncated.slice(0, -1);
          }
          labelToShow = truncated + (truncated.length < label.length ? "..." : "");
        }
        
        doc.text(labelToShow, col1X, yPos);
        
        // Valor por 100g (centro) - alineado a la izquierda de su columna
        doc.text(value100gStr, col2X, yPos);
        
        // Valor por porción (derecha, alineado a la derecha)
        doc.text(valueServingStr, col3X, yPos, { align: "right" });
        
        yPos += smallLineHeight * 0.95; // Un poco más de espacio entre filas
      };
      
      // Agregar todos los campos nutricionales requeridos por MINSAL
      addNutritionRow("Energía (kcal)", "energy_kcal");
      addNutritionRow("Proteína (g)", "proteins_g");
      addNutritionRow("Grasa total (g)", "total_fats_g");
      addNutritionRow("Grasas saturadas (g)", "saturated_fats_g");
      addNutritionRow("Grasas monoinsaturadas (g)", "monounsaturated_fats_g");
      addNutritionRow("Grasas poliinsaturadas (g)", "polyunsaturated_fats_g");
      addNutritionRow("Colesterol (mg)", "cholesterol_mg");
      addNutritionRow("Hidratos de carbono (g)", "carbohydrates_g");
      addNutritionRow("Azúcares totales (g)", "total_sugars_g");
      addNutritionRow("Sodio (mg)", "sodium_mg");
      
      // Guardar PDF
      const fileName = `etiqueta-nutricional-${recipe.code || recipe.id}.pdf`;
      doc.save(fileName);
      
      message.success("PDF descargado exitosamente");
    } catch (error) {
      console.error("Error generando PDF:", error);
      message.error(`Error al generar PDF: ${error.message || "Error desconocido"}`);
    } finally {
      setLoading(false);
    }
  };

  if (!recipe) return null;

  return (
    <Modal
      title="Etiqueta Nutricional - MINSAL"
      open={visible}
      onCancel={onCancel}
      footer={
        <Space>
          {!recipe.has_nutritional_calculation && (
            <Button
              type="primary"
              icon={<CalculatorOutlined />}
              onClick={handleCalculate}
              loading={calculating}
            >
              Calcular Nutrición
            </Button>
          )}
          {recipe.has_nutritional_calculation && (
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              onClick={handleDownloadPDF}
              loading={loading}
            >
              Descargar PDF
            </Button>
          )}
          <Button onClick={onCancel}>Cerrar</Button>
        </Space>
      }
      width={600}
    >
      {loading && !labelData ? (
        <Spin size="large" style={{ display: "block", textAlign: "center", padding: 40 }} />
      ) : !recipe.has_nutritional_calculation ? (
        <div style={{ textAlign: "center", padding: 40 }}>
          <Text type="warning">
            Esta receta no tiene valores nutricionales calculados.
            Haz clic en "Calcular Nutrición" para generar la etiqueta.
          </Text>
        </div>
      ) : labelData ? (
        <Card>
          <Title level={4} style={{ textAlign: "center", marginBottom: 24 }}>
            {labelData.recipe_name}
          </Title>
          
          {labelData.recipe_code && (
            <Text type="secondary" style={{ display: "block", textAlign: "center", marginBottom: 16 }}>
              Código: {labelData.recipe_code}
            </Text>
          )}

          <Divider>INFORMACIÓN NUTRICIONAL</Divider>
          <Text type="secondary" style={{ display: "block", textAlign: "center", marginBottom: 16 }}>
            Por 100g de producto
          </Text>

          <Row gutter={[16, 16]}>
            <Col span={12}>
              <Text strong>Valor Energético:</Text>
            </Col>
            <Col span={12} style={{ textAlign: "right" }}>
              <Text>{labelData.values_per_100g.energy_kcal?.toFixed(2) || "-"} kcal</Text>
            </Col>

            <Col span={12}>
              <Text strong>Proteínas:</Text>
            </Col>
            <Col span={12} style={{ textAlign: "right" }}>
              <Text>{labelData.values_per_100g.proteins_g?.toFixed(2) || "-"} g</Text>
            </Col>

            <Col span={12}>
              <Text strong>Grasas Totales:</Text>
            </Col>
            <Col span={12} style={{ textAlign: "right" }}>
              <Text>{labelData.values_per_100g.total_fats_g?.toFixed(2) || "-"} g</Text>
            </Col>

            <Col span={12}>
              <Text>Grasas Saturadas:</Text>
            </Col>
            <Col span={12} style={{ textAlign: "right" }}>
              <Text>{labelData.values_per_100g.saturated_fats_g?.toFixed(2) || "-"} g</Text>
            </Col>

            <Col span={12}>
              <Text>Grasas Monoinsaturadas:</Text>
            </Col>
            <Col span={12} style={{ textAlign: "right" }}>
              <Text>{labelData.values_per_100g.monounsaturated_fats_g?.toFixed(2) || "-"} g</Text>
            </Col>

            <Col span={12}>
              <Text>Grasas Poliinsaturadas:</Text>
            </Col>
            <Col span={12} style={{ textAlign: "right" }}>
              <Text>{labelData.values_per_100g.polyunsaturated_fats_g?.toFixed(2) || "-"} g</Text>
            </Col>

            <Col span={12}>
              <Text strong>Colesterol:</Text>
            </Col>
            <Col span={12} style={{ textAlign: "right" }}>
              <Text>{labelData.values_per_100g.cholesterol_mg?.toFixed(2) || "-"} mg</Text>
            </Col>

            <Col span={12}>
              <Text strong>Hidratos de Carbono:</Text>
            </Col>
            <Col span={12} style={{ textAlign: "right" }}>
              <Text>{labelData.values_per_100g.carbohydrates_g?.toFixed(2) || "-"} g</Text>
            </Col>

            <Col span={12}>
              <Text>Azúcares Totales:</Text>
            </Col>
            <Col span={12} style={{ textAlign: "right" }}>
              <Text>{labelData.values_per_100g.total_sugars_g?.toFixed(2) || "-"} g</Text>
            </Col>

            <Col span={12}>
              <Text strong>Sodio:</Text>
            </Col>
            <Col span={12} style={{ textAlign: "right" }}>
              <Text>{labelData.values_per_100g.sodium_mg?.toFixed(2) || "-"} mg</Text>
            </Col>

            {labelData.values_per_100g.dietary_fiber_g && (
              <>
                <Col span={12}>
                  <Text>Fibra Dietética:</Text>
                </Col>
                <Col span={12} style={{ textAlign: "right" }}>
                  <Text>{labelData.values_per_100g.dietary_fiber_g.toFixed(2)} g</Text>
                </Col>
              </>
            )}
          </Row>

          {labelData.last_calculation && (
            <div style={{ marginTop: 24, textAlign: "center" }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Última actualización: {new Date(labelData.last_calculation).toLocaleString("es-CL")}
              </Text>
            </div>
          )}
        </Card>
      ) : (
        <div style={{ textAlign: "center", padding: 40 }}>
          <Text>No se pudo cargar la etiqueta nutricional</Text>
        </div>
      )}
    </Modal>
  );
};

export default NutritionLabelModal;

